import { useEffect, useState, useCallback } from "react";
import { useAppDispatch } from "hooks";
import { FormLayout, TextField, Form, Button, Banner } from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { updateUserEmail, fetchById } from "services/user-select.service";
import { fetchUserDetails } from "containers/user/user-container.slice";

interface EditEmailFormProps {
  id: string;
}

function EditEmailForm({ id }: EditEmailFormProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailChange = useCallback(
    (value: string) => {
      setEmail(value);
      // Clear error message when user starts typing
      if (errMsg) {
        setErrMsg("");
      }
    },
    [errMsg]
  );

  useEffect(() => {
    async function fetchData() {
      if (id && companyId) {
        try {
          setIsLoading(true);
          const { userEntity } = await fetchById(id, companyId);
          setEmail(userEntity.email || "");
        } catch (e) {
          console.error(e);
          setErrMsg("error_loading_user");
        } finally {
          setIsLoading(false);
        }
      }
    }
    fetchData();
  }, [id, companyId]);

  const handleFormSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) return;

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !companyId || !id) {
      setErrMsg("email_required");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setErrMsg("invalid_email_address");
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);
      setErrMsg("");
      setSuccessMessage("");

      const result = await updateUserEmail(companyId, id, trimmedEmail);

      // Changing your own login email only sends a confirmation link; the
      // address stays as it was until it is opened, so keep the modal open
      // for the person to read it and there is nothing to refresh.
      if (result?.message === "email_confirmation_sent") {
        setSuccessMessage(
          t("email_confirmation_sent", { email: result.pendingEmail })
        );
        return;
      }

      setSuccessMessage(t("email_updated_successfully"));

      // Refresh user data
      if (companyId && id) {
        dispatch(fetchUserDetails({ userId: id, companyId }));
      }

      // Close modal after 1.5 seconds
      setTimeout(() => {
        dispatch(setIsModalxOpen(false));
      }, 1500);
    } catch (error: any) {
      // Map error to user-friendly message
      let errorMessage = "error_updating_email";

      if (error?.response) {
        // Server responded with error
        const status = error.response.status;
        const backendMessage = error.response.data?.message;

        // Check if backend provided a user-friendly translation key
        if (
          backendMessage &&
          backendMessage.indexOf("_") > -1 &&
          !backendMessage.includes(" ")
        ) {
          // Looks like a translation key (e.g., "email_already_exists")
          errorMessage = backendMessage;
        } else if (
          status === 409 ||
          backendMessage?.toLowerCase().includes("already exists")
        ) {
          errorMessage = "email_already_exists";
        } else if (status === 400) {
          errorMessage = "invalid_email_address";
        } else if (status >= 500) {
          errorMessage = "error_updating_email";
        }
      } else if (error?.request) {
        // Network error - request was made but no response
        errorMessage = "network_error";
      }

      setErrMsg(errorMessage);
      setSuccessMessage("");
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const trimmedEmail = email.trim();
    return trimmedEmail && trimmedEmail.length > 0 && !isSubmitting;
  };

  return (
    <div className="edit-email-form">
      <Form onSubmit={handleFormSubmit}>
        <FormLayout>
          <div className="form-warnings">
            {errMsg && (
              <div className="">
                <Banner tone="critical">{t(errMsg)}</Banner>
              </div>
            )}
            {successMessage && (
              <div className="">
                <Banner tone="success">{successMessage}</Banner>
              </div>
            )}
          </div>
          <TextField
            label={t("Email")}
            type="email"
            requiredIndicator
            autoComplete="off"
            value={email}
            onChange={handleEmailChange}
            disabled={isLoading}
          />
          <Button loading={isLoading} disabled={!isFormValid()} submit>
            {t("update_email")}
          </Button>
        </FormLayout>
      </Form>
    </div>
  );
}

export default EditEmailForm;
